import React, {Component} from 'react'
import PropTypes from 'prop-types'
import FormGroup from './FormGroup'
import Title from '../misc/Title'
import ShortCaseInfo from '../misc/ShortCaseInfo'
import {smoothScrollToTop} from '../misc/ScrollToTop'
import {getI18nTextWithContext} from "wipi-common/src/lib/i18n"
import I18nContext from "../i18n/I18nContext"
import "./DataCompletion.scss"
import skylineLight from '../../assets/illustrations/bike-skyline-light.svg'
import skylineDark from '../../assets/illustrations/bike-skyline-dark.svg'

const DATA_COMPLETION_DOCUMENT_TITLE = 'POSTIDENT | '

export default class DataCompletion extends Component {
    static contextType = I18nContext

    state = {
        userData: {},
        isSubmitting: false,
        isEmailPresent: false
    }

    componentDidMount() {
        document.title = DATA_COMPLETION_DOCUMENT_TITLE + getI18nTextWithContext(this.context, 'dataCompletion.title')
        this.setState(() => ({
            isEmailPresent: isEmailPresent(this.props.caseInfo.contactData)
        }))
    }

    componentDidUpdate(prevProps) {
        if (prevProps.serverValidationErrors !== this.props.serverValidationErrors) {
            this.mergeServerValidation()
        }
    }

    mergeServerValidation = () => {
        this.props.serverValidationErrors.forEach(error => {
            const value = this.state.userData[error.parameterName]?.value
            this.validateUserData(error.parameterName, null, value, error.errorText)
        })
    }

    submitData = e => {
        e.preventDefault()
        if (this.state.isSubmitting) {
            return
        }
        const requestedData = this.props.caseInfo.contactData
        this.setSubmitting(true)
        this.validateAllUntouched().then(() => {
            if (isValidForm(this.state.userData, requestedData)) {
                this.props.submitData(preparePayload(this.state.userData, requestedData))
                    .then(() => this.setSubmitting(false))
                    .then(smoothScrollToTop)
            } else {
                this.setSubmitting(false)
            }
        })
    }

    setSubmitting = isSubmitting => this.setState(() => ({isSubmitting}))

    validateAllUntouched = () => {
        const requestedData = this.props.caseInfo.contactData
        return Promise.all(
            requestedData.map(requested => {
                const userData = this.state.userData[requested.key]
                if (requested.mandatory && !userData) {
                    return this.validateUserData(requested.key, requested.mandatory)
                }
                return Promise.resolve()
            })
        )
    }

    validateUserData = (key, mandatory, value, externalErrorText) => {
        let errorText = externalErrorText
        let isI18nKey = false;
        if (mandatory && !value) {
            errorText = 'dataCompletion.validation.missing';
            isI18nKey = true;
        }
        return new Promise(resolve => {
            this.setState(state => ({
                userData: Object.assign({}, state.userData, {
                    [key]: {
                        value,
                        errorText,
                        isI18nKey,
                        validated: !!errorText
                    }
                })
            }), resolve)
        })
    }

    render() {
        const formGroups = this.props.caseInfo.contactData
            .sort(sortData)
            .map(data => {
                const currentUserData = this.state.userData[data.key];
                let displayUserData = currentUserData ? { ...currentUserData } : undefined;
                if (displayUserData && displayUserData.isI18nKey && displayUserData.errorText) {
                    displayUserData.errorText = getI18nTextWithContext(this.context, displayUserData.errorText);
                }
                return <FormGroup
                    data={data}
                    onChange={this.validateUserData.bind(this, data.key, data.mandatory)}
                    key={data.key}
                    enteredUserData={displayUserData}
                />
            })
        return <>
            <div className="row">
                <div className="col-xl-4 offset-xl-4 col-lg-6 offset-lg-3 col-md-6 offset-md-3">
                    <Title heading={getI18nTextWithContext(this.context, 'dataCompletion.heading')}
                           subHeading={getI18nTextWithContext(this.context, 'dataCompletion.subHeading')}
                           testId="70a6d638-d0ef"/>
                    <div className="data-complete-frame px-4 py-4 text-center">
                        <form onSubmit={this.submitData}>
                            {formGroups}
                            {this.state.isEmailPresent && (
                                <small>{getI18nTextWithContext(this.context, 'dataCompletion.validation.email.helper.text')}</small>
                            )}
                            <button
                                className="btn btn-primary w-100 d-flex mt-3 position-relative justify-content-center align-items-center"
                                disabled={this.state.isSubmitting}
                                data-test-id="03d90225-b547"
                                aria-label={getI18nTextWithContext(this.context, 'common.continue')}>
                                {getI18nTextWithContext(this.context, 'common.continue')}
                                <svg className="position-absolute end-0 me-3" width="16" height="16" viewBox="0 0 16 16"
                                     xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                    <path fill="currentcolor" id="arrow-right-icon"
                                          d="M0 9H12.17L6.58 14.59L8 16L16 8L8 0L6.59 1.41L12.17 7H0L0 9Z"/>
                                </svg>
                            </button>
                        </form>
                    </div>
                </div>
            </div>
            <div className="row pt-3">
                <div className="col col-xl-4 offset-xl-4 col-lg-6 offset-lg-3 col-md-6 offset-md-3">
                    <img id="skyline-light" src={skylineLight} alt="Skyline"
                         data-test-id="58gb735f-9ff1"/>
                    <img id="skyline-dark" src={skylineDark} alt="Skyline"
                         data-test-id="58gb735f-9ff1"/>
                </div>
            </div>
            <ShortCaseInfo bcName={this.props.caseInfo.principalDisplayName}/>
        </>
    }
}

DataCompletion.propTypes = {
    caseInfo: PropTypes.shape({
        contactData: PropTypes.arrayOf(
            PropTypes.shape({
                sortKey: PropTypes.number.isRequired
            })
        ).isRequired,
        principalDisplayName: PropTypes.string.isRequired
    }).isRequired,
    submitData: PropTypes.func.isRequired,
    serverValidationErrors: PropTypes.arrayOf(
        PropTypes.shape({
            parameterName: PropTypes.string.isRequired,
            errorText: PropTypes.string.isRequired
        })
    )
}

function isValidForm(userData, requestedData) {
    return requestedData
        .reduce((accumulator, requested) => accumulator && isValidData(requested, userData[requested.key]), true)
}

function isValidData(requested, userData) {
    return !userData?.errorText
}

function sortData(d1, d2) {
    return d1.sortKey - d2.sortKey
}

function isEmailPresent(requestedData) {
    return requestedData.some(data => data.key === 'email');
}

function preparePayload(userData, requestedData) {
    const payload = {}
    Object.keys(userData).forEach(key => payload[key] = userData[key].value)
    requestedData.forEach(data => {
        payload[data.key + 'Provided'] = !!payload[data.key]
    })
    return payload
}